import Highcharts from 'highcharts'
import createExtendedPlotline, { ExtendedPlotline, ExtendedPlotLineOptions } from './extended-plotline'

export { ExtendedPlotline as HighchartsMarker } from './extended-plotline'

export interface CompleteAxis extends Highcharts.Axis {
    plotLinesAndBands: ExtendedPlotline[]
    visible: boolean
    redraw: (args: unknown) => unknown
}

/**
 * Common plotline/marker manipulation
 */
export default class ChartMarker {
    /**
     * Add a new marker to a given chart.
     *
     * @param chart Where to add this new marker
     * @param options Marker options
     */
    addMarker(chart: Highcharts.Chart, options: ExtendedPlotLineOptions): ExtendedPlotLineOptions | null {
        const axis = ChartMarker.getXAxis(chart)

        if (options.id !== 'placeholder' && axis.getExtremes != null && options.value < axis.getExtremes().min) {
            return null
        }

        const plotLineOptions = {
            className: 'markers',
            width: 1,
            zIndex: 10,
            ...options,
            color: '#F86800'
        }

        const existing = this.findMarker(chart, options.id)
        if (existing) {
            // ignore duplicates to avoid duplicating plot lines
            // placeholder marker doesn't have annotation id
            return existing.options!
        }

        // Highcharts does not allow creation of plotlines
        // in non visible xAxis, force it anyway.
        // @TODO: Test if marker is properly removed
        const needsFixUp = !axis.visible

        if (needsFixUp) axis.visible = true

        const plotline = axis.addPlotLine(plotLineOptions) as ExtendedPlotline

        if (needsFixUp) {
            axis.visible = false

            if (axis.redraw !== updateXAxisPlotlines) {
                axis.redraw = updateXAxisPlotlines
            }
        }

        if (plotline.svgElem == null) {
            console.error('Plotline was not added to axis')

            return null
        }

        this.incrementInternalCount(chart, 1)

        this.setUpMarker(chart as Highcharts.Chart & { widgetId: string }, plotline)

        return plotline.options!
    }

    /**
     * Updates the marker position in a given chart.
     *
     * @param chart Chart that will have its marker updated.
     * @param id Which marker should be updated.
     * @param options New options value.
     */
    updateMarkerPosition(
        chart: Highcharts.Chart,
        id: string,
        options: { value: number }
    ): ExtendedPlotline['options'] | null {
        const marker = this.findMarker(chart, id)

        if (marker == null || options.value == null) return null

        marker.options.value = options.value as number
        marker.options.data.value = options.value as number
        marker.render()

        return marker.options
    }

    /**
     * Updates the marker position in a given chart.
     *
     * @param chart Chart that will have its marker updated.
     * @param id Which marker should be updated.
     * @param options New options value.
     */
    updateMarker(
        chart: Highcharts.Chart,
        id: string,
        options: ExtendedPlotline['options']
    ): ExtendedPlotline['options'] | null {
        const marker = this.findMarker(chart, id)

        if (marker == null || options.value == null) return null

        marker.options = { ...options }
        marker.options.id = id
        marker.render()

        return marker.options
    }

    /**
     * Finds a marker with a given id in a given chart.
     *
     * @param chart Where to look for this marker
     * @param id Marker id
     * @returns Marker with provided id, null otherwise
     */
    findMarker(chart: Highcharts.Chart, id: string): ExtendedPlotline | undefined {
        const axis = ChartMarker.getXAxis(chart)

        return axis.plotLinesAndBands.find(({ options }) => options.id === id)
    }

    /**
     * Removes a given marker from a given chart.
     *
     * @param chart Chart to remove marker from
     * @param id Marker to be removed
     * @returns Options of the removed marker
     */
    removeMarker(chart: Highcharts.Chart, id: string): ExtendedPlotline['options'] | null {
        const marker = this.findMarker(chart, id)

        if (marker == null) return null

        const opts = {
            id: marker.options.id,
            value: marker.options.value,
            data: marker.options.data,
            onShowDetails: () => null,
            onChangePosition: () => null
        }

        ChartMarker.getXAxis(chart).removePlotLine(id)

        this.incrementInternalCount(chart, -1)

        return opts
    }

    /**
     * Tries to get a valid xAxis to include plotlines.
     *
     * @param chart Where to xAxis should be extracted from
     */
    static getXAxis(chart: Highcharts.Chart): CompleteAxis {
        return (chart.xAxis.find((axis: Highcharts.Axis & { visible?: boolean }) => axis.visible) ||
            chart.xAxis[0]) as CompleteAxis
    }

    /**
     * Redefine default plotline render() implementation to also
     * render a custom label, a button and bigger click area.
     *
     */
    private setUpMarker(chart: Highcharts.Chart & { widgetId: string }, plotline: ExtendedPlotline): void {
        createExtendedPlotline(plotline)
    }

    /**
     * We keep track of expected visual markers to detect if they were removed by another part of the system.
     * This might not be needed in future if live-ui's temporal doesn't clear all plotLines it didn't add.
     *
     * @param chart
     * @param inc
     * @private
     */
    private incrementInternalCount(chart: Highcarts.Chart, inc: number) {
        if (!chart.__annotation_markers__) {
            chart.__annotation_markers__ = 0
        }
        chart.__annotation_markers__ = chart.__annotation_markers__ + inc
    }
}

// A x axis where the visible property is false
// dont allow plotlines to be shown.
// This is a fixup for this.
function updateXAxisPlotlines(this: CompleteAxis, ...args: unknown[]): unknown {
    const result = Object.getPrototypeOf(this).redraw.call(this, ...args)

    for (const plotline of this.plotLinesAndBands) {
        if (plotline.timestamp != null) {
            plotline.render()
        }
    }

    return result
}
